import {
  SESSION_REPLAY_LIMITS,
  type SessionReplayChunkRequest,
  type SessionReplayConfig,
  type SessionReplaySignal,
} from "@backchannel/tax-contracts";
import type { TaxApiClient } from "~/lib/api/client";
import { TaxApiError } from "~/lib/api/errors";
import {
  MAX_CHUNK_RETRIES,
  jsonByteLength,
  retryDelayMs,
  selectChunk,
  shouldRetry,
} from "./chunks";
import { isSessionReplayAllowed } from "./enabled";
import { scrubEvents } from "./scrub";
import { REPLAY_BLOCK_SELECTOR, REPLAY_MASK_SELECTOR } from "./sensitive";
import {
  configureReplaySession,
  currentReplaySessionId,
  nextReplaySequence,
  onRotate,
  rotateReplaySession,
  touchReplaySession,
} from "./session-id";
import { drainSignals, pendingSignalCount, setSignalsAccepting } from "./signals";

type RrwebEvent = Record<string, unknown> & { type?: number; timestamp?: number };

type RecordStop = () => void;

type RrwebRecord = ((options: Record<string, unknown>) => RecordStop | undefined) & {
  takeFullSnapshot?: (isCheckout?: boolean) => void;
};

type RecorderApi = Pick<TaxApiClient, "getSessionReplayConfig" | "uploadSessionReplayChunk">;

const QUEUE_EVENT_CAP = 5_000;
const QUEUE_BYTE_CAP = 5 * 1024 * 1024;
const KEEPALIVE_MAX_BYTES = 60 * 1024;
const FULL_SNAPSHOT = 2;
const SIZE_CHECK_EVERY = 25;

let active = false;
let disposed = false;
let config: SessionReplayConfig | null = null;
let configRetryTimer: ReturnType<typeof setTimeout> | null = null;
let flushTimer: ReturnType<typeof setInterval> | null = null;
let stopRecording: RecordStop | null = null;
let takeFullSnapshot: ((isCheckout?: boolean) => void) | null = null;
let recordingEnabled = false;
let pausedForAuth = false;
let eventQueue: RrwebEvent[] = [];
let estimatedBytes = 0;
let eventsSinceSizeCheck = 0;
let sendChain: Promise<void> = Promise.resolve();
let halvedOnce = false;
let apiRef: RecorderApi | null = null;
let buildVersion: string | undefined;
let disposeRotate: (() => void) | null = null;
let lastRoute = "/";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function routePathname(): string {
  try {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    const cut = path.search(/[?#]/);
    const cleaned = cut === -1 ? path : path.slice(0, cut);
    return cleaned.startsWith("/") ? cleaned : `/${cleaned}`;
  } catch {
    return "/";
  }
}

function isAuthRoute(pathname = routePathname()): boolean {
  return pathname === "/auth" || pathname.startsWith("/auth/");
}

async function isSampled(replaySessionId: string, sampleRate: number): Promise<boolean> {
  if (sampleRate >= 1) return true;
  if (sampleRate <= 0) return false;
  try {
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(replaySessionId));
    const bucket = new DataView(digest).getUint32(0) / 2 ** 32;
    return bucket < sampleRate;
  } catch {
    return false;
  }
}

function trimQueue(): void {
  while (
    (eventQueue.length > QUEUE_EVENT_CAP || estimatedBytes > QUEUE_BYTE_CAP) &&
    eventQueue.length > 0
  ) {
    const dropAt = eventQueue.findIndex((event) => event.type !== FULL_SNAPSHOT);
    const index = dropAt === -1 ? 0 : dropAt;
    const [removed] = eventQueue.splice(index, 1);
    if (removed) estimatedBytes = Math.max(0, estimatedBytes - jsonByteLength(removed));
  }
}

function enqueue(event: RrwebEvent): void {
  eventQueue.push(event);
  estimatedBytes += jsonByteLength(event);
  eventsSinceSizeCheck += 1;
  trimQueue();
  const maxEvents = config?.maxEventsPerChunk ?? SESSION_REPLAY_LIMITS.maxEventsPerChunk;
  const maxBytes = config?.maxChunkBytes ?? SESSION_REPLAY_LIMITS.maxChunkBytes;
  if (
    eventQueue.length >= maxEvents ||
    (eventsSinceSizeCheck >= SIZE_CHECK_EVERY && estimatedBytes >= maxBytes * 0.9)
  ) {
    eventsSinceSizeCheck = 0;
    void sealAndSend();
  }
}

function viewport() {
  try {
    return {
      width: Math.max(1, Math.min(20_000, window.innerWidth || 1)),
      height: Math.max(1, Math.min(20_000, window.innerHeight || 1)),
    };
  } catch {
    return undefined;
  }
}

function timeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function eventTime(event: RrwebEvent | undefined, fallback: number): number {
  return typeof event?.timestamp === "number" && Number.isFinite(event.timestamp)
    ? event.timestamp
    : fallback;
}

function buildChunk(
  events: RrwebEvent[],
  signals: SessionReplaySignal[],
  maxEvents: number,
): SessionReplayChunkRequest | null {
  if (events.length === 0 && signals.length === 0) return null;
  const now = Date.now();
  const startedAt = eventTime(events[0], now);
  const endedAt = eventTime(events[events.length - 1], Math.max(startedAt, now));
  let replaySessionId = touchReplaySession();
  let sequence = nextReplaySequence(replaySessionId);
  if (sequence >= SESSION_REPLAY_LIMITS.maxChunksPerSession) {
    // A long working day: continue in a fresh session (the server caps chunks per session).
    replaySessionId = rotateReplaySession();
    sequence = nextReplaySequence(replaySessionId);
    // The new session needs its own full snapshot; it lands in the next chunk.
    try {
      takeFullSnapshot?.(true);
    } catch {
      /* the next interval still uploads incremental events */
    }
  }
  return {
    replaySessionId,
    chunkId: crypto.randomUUID(),
    sequence,
    startedAt: new Date(startedAt).toISOString(),
    endedAt: new Date(Math.max(endedAt, startedAt)).toISOString(),
    route: lastRoute || routePathname(),
    buildVersion,
    locale: typeof navigator !== "undefined" ? navigator.language : undefined,
    timeZone: timeZone(),
    viewport: viewport(),
    events: scrubEvents(events).slice(0, maxEvents),
    signals: signals.slice(0, SESSION_REPLAY_LIMITS.maxSignalsPerChunk),
  };
}

function takeEvents(maxBytes: number, maxEvents: number): RrwebEvent[] {
  const { selected, remaining } = selectChunk(eventQueue, { maxBytes, maxEvents });
  eventQueue = remaining;
  estimatedBytes = remaining.reduce((sum, event) => sum + jsonByteLength(event), 0);
  eventsSinceSizeCheck = 0;
  return selected;
}

async function sendChunk(
  body: SessionReplayChunkRequest,
  options?: { keepalive?: boolean },
): Promise<"ok" | "retry" | "split" | "drop"> {
  if (!apiRef) return "drop";
  let attempt = 0;
  while (attempt <= MAX_CHUNK_RETRIES) {
    try {
      await apiRef.uploadSessionReplayChunk(body, { keepalive: options?.keepalive });
      return "ok";
    } catch (error) {
      const status = error instanceof TaxApiError ? error.status : undefined;
      if (status === 413) return "split";
      if (!shouldRetry(status === 0 ? undefined : status)) return "drop";
      if (attempt >= MAX_CHUNK_RETRIES) return "drop";
      const retryAfter = error instanceof TaxApiError ? error.retryAfterSeconds : undefined;
      await sleep(retryDelayMs(attempt, retryAfter));
      attempt += 1;
    }
  }
  return "drop";
}

function enqueueSend(task: () => Promise<void>): void {
  sendChain = sendChain.then(task).catch(() => undefined);
}

function sealAndSend(options?: { keepalive?: boolean; maxBytes?: number }): void {
  if (!apiRef || disposed) return;
  const maxEvents = halvedOnce
    ? Math.max(
        1,
        Math.floor((config?.maxEventsPerChunk ?? SESSION_REPLAY_LIMITS.maxEventsPerChunk) / 2),
      )
    : (config?.maxEventsPerChunk ?? SESSION_REPLAY_LIMITS.maxEventsPerChunk);
  const maxBytes =
    options?.maxBytes ?? config?.maxChunkBytes ?? SESSION_REPLAY_LIMITS.maxChunkBytes;

  const events = takeEvents(maxBytes, maxEvents);
  const signals = drainSignals();
  const chunk = buildChunk(events, signals, maxEvents);
  if (!chunk) return;

  if (options?.keepalive) {
    // The page is going away: a chained send could wait behind a retry and never leave.
    void apiRef.uploadSessionReplayChunk(chunk, { keepalive: true }).catch(() => undefined);
    return;
  }
  enqueueSend(async () => {
    const result = await sendChunk(chunk, options);
    if (result === "split") {
      eventQueue = [...(chunk.events as RrwebEvent[]), ...eventQueue];
      estimatedBytes = eventQueue.reduce((sum, event) => sum + jsonByteLength(event), 0);
      if (!halvedOnce) {
        halvedOnce = true;
        sealAndSend(options);
      }
      // After one split retry, drop (do not requeue again).
    }
  });
}

function stopRrweb(): void {
  try {
    stopRecording?.();
  } catch {
    /* ignore */
  }
  stopRecording = null;
  takeFullSnapshot = null;
}

function clearQueue(): void {
  eventQueue = [];
  estimatedBytes = 0;
  eventsSinceSizeCheck = 0;
}

async function startRrweb(): Promise<void> {
  if (!recordingEnabled || pausedForAuth || stopRecording || disposed) return;
  try {
    const mod = await import("rrweb");
    const record = mod.record as RrwebRecord;
    takeFullSnapshot =
      typeof record.takeFullSnapshot === "function" ? record.takeFullSnapshot : null;
    stopRecording =
      record({
        emit: (event: RrwebEvent) => {
          if (pausedForAuth || disposed || !recordingEnabled) return;
          enqueue(event);
        },
        maskAllInputs: true,
        maskInputOptions: { password: true },
        blockSelector: REPLAY_BLOCK_SELECTOR,
        maskTextSelector: REPLAY_MASK_SELECTOR,
        recordCanvas: false,
        collectFonts: false,
        inlineImages: false,
        inlineStylesheet: true,
        recordCrossOriginIframes: false,
        slimDOMOptions: "all",
        sampling: { mousemove: 50, scroll: 150, media: 800, input: "last" },
        errorHandler: () => true,
      }) ?? null;
  } catch {
    recordingEnabled = false;
  }
}

function onHidden(): void {
  if (eventQueue.length === 0 && pendingSignalCount() === 0) return;
  sealAndSend({ keepalive: true, maxBytes: KEEPALIVE_MAX_BYTES });
}

function onVisibilityChange(): void {
  if (document.visibilityState === "hidden") onHidden();
}

function pauseForAuthRoute(): void {
  if (pausedForAuth) return;
  pausedForAuth = true;
  stopRrweb();
  clearQueue();
}

function resumeFromAuthRoute(): void {
  if (!pausedForAuth) return;
  pausedForAuth = false;
  void startRrweb();
}

function handleRouteChange(pathname: string): void {
  lastRoute = pathname;
  if (isAuthRoute(pathname)) {
    pauseForAuthRoute();
    return;
  }
  resumeFromAuthRoute();
  if (eventQueue.length > 0 || pendingSignalCount() > 0) {
    sealAndSend();
  }
}

async function applyConfig(next: SessionReplayConfig): Promise<void> {
  config = next;
  configureReplaySession({ idleRotationMs: next.idleRotationMs });
  if (!next.signalsEnabled) {
    setSignalsAccepting(false);
    stopFully();
    return;
  }
  setSignalsAccepting(true);
  const sessionId = currentReplaySessionId();
  recordingEnabled = next.enabled && (await isSampled(sessionId, next.sampleRate));
  if (recordingEnabled && !isAuthRoute()) {
    await startRrweb();
  }
  if (!flushTimer) {
    const interval = next.flushIntervalMs || SESSION_REPLAY_LIMITS.flushIntervalMs;
    flushTimer = setInterval(() => {
      if (eventQueue.length > 0 || pendingSignalCount() > 0) sealAndSend();
    }, interval);
  }
}

function stopFully(): void {
  stopRrweb();
  clearQueue();
  recordingEnabled = false;
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
}

async function loadConfig(api: RecorderApi): Promise<void> {
  try {
    const next = await api.getSessionReplayConfig();
    await applyConfig(next);
  } catch {
    if (configRetryTimer || disposed) return;
    configRetryTimer = setTimeout(() => {
      configRetryTimer = null;
      void loadConfig(api);
    }, 60_000);
  }
}

/**
 * Start the recorder after the authenticated shell is up. Idempotent.
 */
export function startSessionRecorder(options: {
  api: RecorderApi;
  buildVersion?: string;
  getPathname?: () => string;
}): () => void {
  if (active || !isSessionReplayAllowed()) {
    return () => undefined;
  }
  active = true;
  disposed = false;
  apiRef = options.api;
  buildVersion = options.buildVersion;
  lastRoute = options.getPathname?.() ?? routePathname();
  halvedOnce = false;

  disposeRotate = onRotate(() => {
    sealAndSend();
    try {
      takeFullSnapshot?.(true);
    } catch {
      /* ignore */
    }
  });

  window.addEventListener("pagehide", onHidden);
  document.addEventListener("visibilitychange", onVisibilityChange);

  if (isAuthRoute(lastRoute)) pauseForAuthRoute();

  void loadConfig(options.api);

  return () => {
    disposed = true;
    active = false;
    window.removeEventListener("pagehide", onHidden);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    disposeRotate?.();
    disposeRotate = null;
    if (configRetryTimer) {
      clearTimeout(configRetryTimer);
      configRetryTimer = null;
    }
    stopFully();
    apiRef = null;
  };
}

/** Pause / clear on sign-out; call {@link startSessionRecorder} again after sign-in. */
export function stopSessionRecorder(): void {
  disposed = true;
  active = false;
  window.removeEventListener("pagehide", onHidden);
  document.removeEventListener("visibilitychange", onVisibilityChange);
  disposeRotate?.();
  disposeRotate = null;
  if (configRetryTimer) {
    clearTimeout(configRetryTimer);
    configRetryTimer = null;
  }
  stopFully();
  apiRef = null;
}

/**
 * Page exit before the recorder started (it starts when the shell is idle): send any queued
 * signals in one keepalive chunk so a quick reload does not lose them. When the recorder is
 * running its own pagehide/visibility handler does this.
 */
export function flushSignalsOnExit(api: RecorderApi): void {
  if (active) return;
  if (pendingSignalCount() === 0) return;
  const signals = drainSignals();
  const chunk = buildChunk([], signals, SESSION_REPLAY_LIMITS.maxEventsPerChunk);
  if (!chunk) return;
  void api.uploadSessionReplayChunk(chunk, { keepalive: true }).catch(() => undefined);
}

/** Notify the recorder of a client route change (pathname only). */
export function notifyReplayRoute(pathname: string): void {
  if (!active || disposed) return;
  handleRouteChange(pathname);
}

export function getReplayConfig(): SessionReplayConfig | null {
  return config;
}
